<script setup lang="ts">
import { useDebounceFn, useVModel } from '@vueuse/core'
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
  type AcceptableValue
} from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { BASE_CLASS, BASE_INVALID_CLASS } from '../Input/variant'

const props = withDefaults(
  defineProps<{
    id?: string
    placeholder?: string
    icon?: string
    required?: boolean
    modelValue?: AcceptableValue
    nullable?: boolean
    loadingIcon?: string
    valueKey?: string
    title?: string
    class?: HTMLAttributes['class']
    labelKey?: string
    options?: Record<string, AcceptableValue | undefined>[]
    search?: (
      query: string
    ) => Promise<Record<string, AcceptableValue>[] | undefined>
    invalid?: boolean
  }>(),
  {
    id: undefined,
    placeholder: 'Cari di sini...',
    modelValue: undefined,
    icon: 'material-symbols:keyboard-arrow-down',
    title: undefined,
    options: undefined,
    loadingIcon: 'svg-spinners:90-ring-with-bg',
    valueKey: 'id',
    labelKey: 'name',
    search: undefined,
    class: undefined,
    invalid: false
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>()

const list = ref<Record<string, AcceptableValue | undefined>[]>(
  props.options ?? []
)
watch(
  () => props.options,
  (newOptions) => {
    if (props.search) return

    list.value = newOptions ?? []
  }
)
const content = useVModel(props, 'modelValue', emit)
const query = ref(props.title ?? '')
const loading = ref(false)

const onSearch = useDebounceFn(
  async (searchQuery: string) => {
    if (!props.search) return

    list.value = []
    if (!searchQuery) return

    loading.value = true
    try {
      const fetchList = await props.search(searchQuery)
      list.value = fetchList || []
    } catch (error) {
      console.error('Search error:', error)
      list.value = []
    } finally {
      loading.value = false
    }
  },
  500,
  {
    maxWait: 3000
  }
)

const displayValue = (value: number) => {
  const selected = list.value.find((item) => item[props.valueKey] === value)
  return selected
    ? (selected[props.labelKey]?.toString() ?? props.title ?? '')
    : (props.title ?? '')
}

const inputRef = useTemplateRef('input')
defineExpose({
  inputRef
})
</script>

<template>
  <ComboboxRoot
    v-model="content"
    :ignore-filter="!!props.search"
    class="relative"
  >
    <ComboboxAnchor
      class="w-full inline-flex items-center justify-between text-sm relative"
    >
      <ComboboxInput
        :id="props.id"
        ref="input"
        v-model="query"
        :display-value="displayValue"
        :required="props.required"
        :class="
          cn(
            BASE_CLASS,
            'pr-8',
            {
              [BASE_INVALID_CLASS]: props.invalid
            },
            props.class
          )
        "
        :placeholder="props.placeholder"
        @input="onSearch(query)"
      />
      <ComboboxTrigger
        class="absolute text-primary right-3 top-1/2 -translate-y-1/2"
      >
        <Icon :name="loading ? loadingIcon : props.icon" size="20px" />
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxPortal>
      <ComboboxContent
        class="max-h-60 transition-all shadow overflow-auto bg-background rounded-lg border border-outline-variant z-100"
        :body-lock="false"
        position="popper"
        :side-offset="4"
        :style="{ width: 'var(--reka-combobox-trigger-width)' }"
      >
        <ComboboxViewport class="p-2 space-y-1">
          <ComboboxEmpty
            class="text-sm font-medium flex gap-3 p-0.5 items-center"
            :class="{
              'text-primary animate-pulse': loading,
              'text-error': !loading,
              'text-info': query == ''
            }"
          >
            <Icon
              class="shrink-0"
              size="20px"
              :name="
                loading
                  ? loadingIcon
                  : query === ''
                    ? 'material-symbols:info'
                    : 'material-symbols:close'
              "
            />
            {{
              loading
                ? 'Mencari data...'
                : query.length < 3
                  ? 'Minimal 3 huruf'
                  : 'Tidak ada hasil'
            }}
          </ComboboxEmpty>

          <ComboboxItem
            v-for="(option, index) in props.nullable
              ? [
                  {
                    [props.valueKey]: null,
                    [props.labelKey]: 'Semua Pilihan'
                  },
                  ...list
                ]
              : list"
            :key="`option-${index}`"
            :value="option[props.valueKey] ?? null"
            class="rounded data-highlighted:bg-primary-container data-[state='checked']:bg-primary-container data-[state='checked']:text-primary flex items-center py-2.5 text-sm hover:bg-surface-container-low pl-8 cursor-pointer pr-3.5 relative select-none"
          >
            <ComboboxItemIndicator class="absolute left-1.5">
              <Icon name="material-symbols:check" size="20px" />
            </ComboboxItemIndicator>
            <span>
              {{ option[props.labelKey] }}
            </span>
          </ComboboxItem>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxPortal>
  </ComboboxRoot>
</template>
